// Ported verbatim from deexoptions-mobile-app/utils/groupByDate.ts

type GroupedData = {
  title: string;
  data: any[];
};

/**
 * Section headers for the activity lists.
 *
 * Buckets are strictly calendar-based: "Today", "This month", then one section
 * per earlier month. The previous implementation used a rolling 30-day window
 * ("Last 30 days"), which straddled the month boundary — on Jul 30 the window
 * opened on Jun 30, so that day was filed with July's transactions and the
 * "Jun" section appeared to end on the 29th. A calendar bucket can't overlap
 * the month below it, so a transaction always lands in its own month.
 *
 * `now` is injectable for tests; callers pass data only.
 */
const monthTitle = (date: Date) =>
  `${date.toLocaleString("default", { month: "short" })}, ${date.getFullYear()}`;

export const groupByDate = (
  data?: any[],
  now: Date = new Date()
): GroupedData[] => {
  if (!data) {
    return [];
  }
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const groups: { [key: string]: any[] } = {};

  data.forEach((item) => {
    const itemDate = new Date(item.createdAt);
    let groupTitle: string;

    if (itemDate >= startOfToday) {
      groupTitle = "Today";
    } else if (itemDate >= startOfMonth) {
      groupTitle = "This month";
    } else {
      groupTitle = monthTitle(itemDate);
    }

    if (!groups[groupTitle]) {
      groups[groupTitle] = [];
    }
    groups[groupTitle].push(item);
  });

  return Object.keys(groups).map((key) => ({
    title: key,
    data: groups[key],
  }));
};
